import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
  SafeAreaView,
  Dimensions,
  Modal,
  Linking
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getMyCoupons, updateCouponVoucherUrl, getCouponImageSource } from '../services/couponsApi';
import AppHeader from '../components/AppHeader';
import {
  getPaygiftUserFacingCopy,
  paygiftMerchantPendingUi,
  isPaygiftStockShortagePending,
  isPaygiftNotAvailablePending,
  isPaygiftApiDownPending,
  isPaygiftAutoRetryPending,
  isPaygiftDepositOrOpsPending,
  getPaygiftPendingWaitUi,
  getPaygiftResolvedBody,
} from '../utils/paygiftUserFacingMessage';

const { width, height } = Dimensions.get('window');

/** Quiet poll while waiting for Aldmic callback (merchant pending / shortage). */
const PENDING_POLL_MS = 5000;
const PENDING_POLL_MAX = 24; // ~2 minutes at 5s interval

/** Catalog end date: nested coupon.tanggal_berakhir beats root expiry fields. */
function getCatalogExpiryFromClaim(c) {
  if (!c || typeof c !== 'object') return null;
  const candidates = [
    c.expiry_date,
    c.endDate,
    c.tanggal_berakhir,
    c.coupon?.tanggal_berakhir,
    c.coupon?.tanggal_selesai,
  ];
  for (const v of candidates) {
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return null;
}

/** Display line: prefer voucher_expiry_date, else catalog date. */
function getValidUntilDisplay(c) {
  if (!c || typeof c !== 'object') return null;
  const ve = c.voucher_expiry_date;
  if (ve != null && String(ve).trim() !== '') {
    return String(ve).trim();
  }
  return getCatalogExpiryFromClaim(c);
}

function paygiftOrderRef(c) {
  if (!c || typeof c !== 'object') return '';
  const v = c.paygift_ref_id ?? c.external_ref_id;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

function paygiftTrxId(c) {
  if (!c || typeof c !== 'object') return '';
  const v = c.paygift_transaction_id ?? c.paygift_trx_id;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

export default function PaygiftCouponDetailsScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { coupon: initialCoupon } = route.params || {};
  
  const [coupon, setCoupon] = useState(initialCoupon || {});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [voucherError, setVoucherError] = useState(null);
  const [pollExhausted, setPollExhausted] = useState(false);
  const couponRef = useRef(coupon);
  couponRef.current = coupon;
  const pollCountRef = useRef(0);
  const loadInFlightRef = useRef(false);
  /** Notify once when poll/refresh finds voucher_url. */
  const voucherReadyNotifiedRef = useRef(false);

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error' or 'success'
    onConfirm: null
  });

  // Custom Alert Functions
  const showCustomAlert = (title, message, type = 'error', onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const hideCustomAlert = () => {
    setAlertModal({
      visible: false,
      title: '',
      message: '',
      type: 'error',
      onConfirm: null
    });
  };

  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
  };

  const isPaygiftType =
    !!(coupon.paygift ||
      coupon.isPaygift ||
      coupon.tipe_kupon === 'paygift' ||
      coupon.type === 'paygift');

  const hasVoucherUrl = !!(coupon.voucher_url && String(coupon.voucher_url).trim());
  const hasVoucherExpiry = !!(
    coupon.voucher_expiry_date && String(coupon.voucher_expiry_date).trim()
  );

  const couponDetailId = coupon.claimId || coupon.claim_id;

  const validUntilDisplay = getValidUntilDisplay(coupon);

  const canSyncPaygiftDetail =
    !!paygiftOrderRef(coupon) && !!paygiftTrxId(coupon);

  const showStockShortageUi =
    !voucherError &&
    isPaygiftType &&
    !!couponDetailId &&
    !hasVoucherUrl &&
    isPaygiftStockShortagePending(coupon);

  const showNotAvailableUi =
    !voucherError &&
    isPaygiftType &&
    !!couponDetailId &&
    !hasVoucherUrl &&
    isPaygiftNotAvailablePending(coupon);

  const showApiDownUi =
    !voucherError &&
    isPaygiftType &&
    !!couponDetailId &&
    !hasVoucherUrl &&
    isPaygiftApiDownPending(coupon);

  // Merchant-wait: no voucher URL; missing ref/trx or voucher expiry not set yet
  const showMerchantWaitUi =
    !showStockShortageUi &&
    !showNotAvailableUi &&
    !showApiDownUi &&
    !isPaygiftDepositOrOpsPending(coupon) &&
    !voucherError &&
    isPaygiftType &&
    !!couponDetailId &&
    !hasVoucherUrl &&
    (!hasVoucherExpiry || !canSyncPaygiftDetail);

  const pendingWaitUi =
    getPaygiftPendingWaitUi(coupon) ||
    (showMerchantWaitUi ? paygiftMerchantPendingUi : null);

  // Load latest coupon data from API.
  // quiet: skip full-screen refresh spinner + error alerts (used by poll).
  // Returns { hasUrl: boolean } for callers.
  const loadLatestCouponData = useCallback(async (options = {}) => {
    const { quiet = false } = options;
    if (!quiet) {
      pollCountRef.current = 0;
      setPollExhausted(false);
    }
    if (loadInFlightRef.current && quiet) {
      return { hasUrl: !!(couponRef.current?.voucher_url && String(couponRef.current.voucher_url).trim()) };
    }
    loadInFlightRef.current = true;
    try {
      if (!quiet) {
        setRefreshing(true);
      }
      const token = await AsyncStorage.getItem('userToken');
      
      if (!token) {
        console.log('No token found, skipping coupon data refresh');
        return { hasUrl: false };
      }

      console.log('Loading latest coupon data');
      const current = couponRef.current || {};
      const hadVoucherUrlBefore = !!(current.voucher_url && String(current.voucher_url).trim());
      
      // First update voucher URL for Paygift coupons (skip stock-shortage — no trx yet)
      if (current.paygift || current.isPaygift || current.tipe_kupon === 'paygift' || current.type === 'paygift') {
        try {
          console.log('Updating voucher URL for Paygift coupon');
          const detailId = current.claimId || current.claim_id;
          if (!detailId) {
            console.warn('No claim id for Paygift voucher URL update');
          } else if (isPaygiftAutoRetryPending(current)) {
            console.log('Skip updateCouponVoucherUrl for paygift auto-retry pending');
            setVoucherError(null);
          } else {
            console.log('Calling updateCouponVoucherUrl');
            await updateCouponVoucherUrl(detailId);
            setVoucherError(null);
          }
        } catch (error) {
          console.warn('Voucher URL update failed:', error.message);
          if (!quiet) {
            const copy = getPaygiftUserFacingCopy(error.message);
            setVoucherError(copy.body);
            showCustomAlert(copy.title, copy.body);
          }
        }
      }
      
      const myCouponsResponse = await getMyCoupons();

      if (myCouponsResponse && myCouponsResponse.data && myCouponsResponse.data.coupons && Array.isArray(myCouponsResponse.data.coupons)) {
        const claimedCoupons = myCouponsResponse.data.coupons;
        const myClaimId = current.claimId || current.claim_id;
        const latestCoupon = claimedCoupons.find(
          (c) => String(c.claim_id) === String(myClaimId)
        );

        if (latestCoupon) {
          setCoupon(prevCoupon => ({
            ...prevCoupon,
            voucher_url: latestCoupon.voucher_url || prevCoupon.voucher_url,
            status: latestCoupon.status || prevCoupon.status,
            expiry_date:
              latestCoupon.expiry_date ||
              (latestCoupon.coupon && latestCoupon.coupon.tanggal_berakhir) ||
              prevCoupon.expiry_date,
            tanggal_berakhir:
              (latestCoupon.coupon && latestCoupon.coupon.tanggal_berakhir) ||
              prevCoupon.tanggal_berakhir,
            endDate:
              (latestCoupon.coupon && latestCoupon.coupon.tanggal_berakhir) ||
              prevCoupon.endDate,
            voucher_expiry_date: latestCoupon.voucher_expiry_date || prevCoupon.voucher_expiry_date,
            tipe_kupon:
              (latestCoupon.coupon && latestCoupon.coupon.tipe_kupon) ||
              latestCoupon.tipe_kupon ||
              prevCoupon.tipe_kupon,
            type:
              (latestCoupon.coupon && latestCoupon.coupon.tipe_kupon) ||
              latestCoupon.tipe_kupon ||
              prevCoupon.type ||
              prevCoupon.tipe_kupon,
            coupon: latestCoupon.coupon
              ? { ...prevCoupon.coupon, ...latestCoupon.coupon }
              : prevCoupon.coupon,
            paygift:
              typeof latestCoupon.paygift === 'boolean'
                ? latestCoupon.paygift
                : prevCoupon.paygift,
            paygift_trx_id: latestCoupon.paygift_trx_id ?? prevCoupon.paygift_trx_id,
            paygift_transaction_id:
              latestCoupon.paygift_transaction_id ?? prevCoupon.paygift_transaction_id,
            paygift_ref_id: latestCoupon.paygift_ref_id ?? prevCoupon.paygift_ref_id,
            external_ref_id: latestCoupon.external_ref_id ?? prevCoupon.external_ref_id,
            paygift_pending_reason:
              latestCoupon.paygift_pending_reason ?? prevCoupon.paygift_pending_reason,
            is_claimed: true,
            claim_id: latestCoupon.claim_id || prevCoupon.claim_id,
            claimId: latestCoupon.claim_id || prevCoupon.claimId || prevCoupon.claim_id,
            kawan_activation: latestCoupon.kawan_activation != null
              ? latestCoupon.kawan_activation
              : prevCoupon.kawan_activation,
            voucher_code: latestCoupon.voucher_code || latestCoupon.claim_id || prevCoupon.voucher_code,
            coupon_code: latestCoupon.coupon_code || (latestCoupon.coupon && latestCoupon.coupon.kode_kupon) || prevCoupon.coupon_code
          }));
          const hasUrl = !!(latestCoupon.voucher_url && String(latestCoupon.voucher_url).trim());
          if (hasUrl && !hadVoucherUrlBefore && !voucherReadyNotifiedRef.current) {
            voucherReadyNotifiedRef.current = true;
            const readyUi = getPaygiftPendingWaitUi(current)
              || getPaygiftPendingWaitUi(latestCoupon)
              || paygiftMerchantPendingUi;
            const voucherName =
              latestCoupon.nama_kupon
              || latestCoupon.title
              || current.nama_kupon
              || current.title
              || '';
            showCustomAlert(
              readyUi.resolvedTitle,
              getPaygiftResolvedBody(voucherName),
              'success'
            );
          }
          return { hasUrl };
        } else {
          console.log('Coupon not found in latest API response');
        }
      } else {
        console.log('Unexpected response format');
      }
      return {
        hasUrl: !!(current.voucher_url && String(current.voucher_url).trim()),
      };
    } catch (error) {
      console.error('Error loading latest coupon data:', error);
      if (!quiet) {
        showCustomAlert('Error', 'Failed to load latest coupon data');
      }
      return { hasUrl: false };
    } finally {
      loadInFlightRef.current = false;
      setLoading(false);
      if (!quiet) {
        setRefreshing(false);
      }
    }
  }, []);

  const handleUseCoupon = async () => {
    try {
      if (!coupon.voucher_url) {
        const copy = getPaygiftUserFacingCopy('Voucher URL not available');
        showCustomAlert(copy.title, copy.body);
        return;
      }

      // Check if the URL can be opened
      const canOpen = await Linking.canOpenURL(coupon.voucher_url);
      
      if (canOpen) {
        await Linking.openURL(coupon.voucher_url);
      } else {
        showCustomAlert('Error', 'Cannot open voucher URL');
      }
    } catch (error) {
      console.error('Error opening voucher URL:', error);
      showCustomAlert('Error', 'Failed to open voucher URL');
    }
  };

  useEffect(() => {
    if (initialCoupon) {
      setCoupon(initialCoupon);
      setVoucherError(null);
      pollCountRef.current = 0;
      voucherReadyNotifiedRef.current = false;
      setPollExhausted(false);
      loadLatestCouponData();
    } else {
      setLoading(false);
    }
  }, [initialCoupon, loadLatestCouponData]);

  // Quiet poll while voucher URL still missing (merchant pending / shortage)
  useFocusEffect(
    useCallback(() => {
      pollCountRef.current = 0;
      const id = setInterval(() => {
        const c = couponRef.current || {};
        const hasUrl = !!(c.voucher_url && String(c.voucher_url).trim());
        if (hasUrl || voucherError) {
          return;
        }
        const isPaygift =
          !!(c.paygift || c.isPaygift || c.tipe_kupon === 'paygift' || c.type === 'paygift');
        const detailId = c.claimId || c.claim_id;
        if (!isPaygift || !detailId) {
          return;
        }
        if (pollCountRef.current >= PENDING_POLL_MAX) {
          setPollExhausted(true);
          return;
        }
        pollCountRef.current += 1;
        loadLatestCouponData({ quiet: true });
      }, PENDING_POLL_MS);
      return () => clearInterval(id);
    }, [loadLatestCouponData, voucherError])
  );

  useFocusEffect(
    useCallback(() => {
      if (!initialCoupon) return;
      loadLatestCouponData();
    }, [initialCoupon, loadLatestCouponData])
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#3B82F6" />
          <Text style={styles.loadingText}>Loading coupon details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  const renderVoucherButton = () => {
    if (voucherError) {
      return (
        <View style={styles.voucherPlaceholder}>
          <Ionicons name="alert-circle-outline" size={64} color="#EF4444" />
          <Text style={styles.voucherPlaceholderText}>
            {voucherError}
          </Text>
        </View>
      );
    }

    if (pendingWaitUi) {
      return (
        <View style={styles.voucherContainer}>
          <Ionicons name="time-outline" size={64} color="#EA580C" />
          <Text style={styles.voucherNote}>{pendingWaitUi.footerHint}</Text>
          {pollExhausted && (
            <Text style={styles.pollStoppedNote}>
              Pembaruan otomatis dihentikan. Ketuk Segarkan status untuk cek lagi.
            </Text>
          )}
          <TouchableOpacity
            style={styles.useCouponButton}
            onPress={() => loadLatestCouponData()}
            disabled={refreshing}
            accessibilityRole="button"
            accessibilityLabel="Segarkan status"
          >
            {refreshing ? (
              <ActivityIndicator color="#FFFFFF" style={styles.buttonIconSpacing} />
            ) : (
              <Ionicons
                name="refresh-outline"
                size={24}
                color="#FFFFFF"
                style={styles.buttonIconSpacing}
              />
            )}
            <Text style={styles.useCouponButtonText}>Segarkan status</Text>
          </TouchableOpacity>
        </View>
      );
    }

    if (!coupon.voucher_url) {
      return (
        <View style={styles.voucherPlaceholder}>
          <Ionicons name="link-outline" size={64} color="#94A3B8" />
          <Text style={styles.voucherPlaceholderText}>
            Link voucher belum tersedia. Coba segarkan halaman ini.
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.voucherContainer}>
        <TouchableOpacity
          style={styles.useCouponButton}
          onPress={handleUseCoupon}
        >
          <Ionicons name="gift-outline" size={24} color="#FFFFFF" />
          <Text style={styles.useCouponButtonText}>Gunakan Kupon</Text>
        </TouchableOpacity>
        <Text style={styles.voucherNote}>
          Tekan tombol di atas untuk membuka voucher
        </Text>
      </View>
    );
  };

  const couponImageSource = getCouponImageSource(coupon);

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader
        title="Detail Kupon"
        onBack={() => navigation.goBack()}
        right={(
          <TouchableOpacity onPress={() => loadLatestCouponData()} disabled={refreshing} hitSlop={8}>
            <Ionicons name="refresh" size={22} color={refreshing ? '#94A3B8' : '#1E293B'} />
          </TouchableOpacity>
        )}
      />

      <ScrollView style={styles.content}>
        {/* Coupon Image */}
        <View style={styles.imageContainer}>
          {couponImageSource ? (
            <Image
              source={couponImageSource}
              style={styles.couponImage}
              resizeMode="contain"
            />
          ) : (
            <View style={styles.imagePlaceholder}>
              <Ionicons name="gift-outline" size={64} color="#94A3B8" />
            </View>
          )}
        </View>

        {/* Coupon Details */}
        <View style={styles.detailsContainer}>
          <Text style={styles.couponName}>{coupon.nama_kupon || coupon.nama}</Text>
          
          {coupon.deskripsi && (
            <Text style={styles.couponDescription}>{coupon.deskripsi}</Text>
          )}

         

          {/* Expiry: catalog tanggal_berakhir or voucher_expiry_date */}
          {(validUntilDisplay || pendingWaitUi) && (
            <View style={styles.expiryBlock}>
              {!!validUntilDisplay && (
                <View style={styles.expiryContainer}>
                  <Ionicons name="time-outline" size={16} color="#6B7280" />
                  <Text style={styles.expiryText}>Berlaku hingga: {validUntilDisplay}</Text>
                </View>
              )}
              {pendingWaitUi && (
                <Text style={styles.expiryPendingHint}>
                  {validUntilDisplay
                    ? pendingWaitUi.expiryHint
                    : pendingWaitUi.expiryHintNoDate}
                </Text>
              )}
            </View>
          )}

          {/* Status */}
          <View style={styles.statusContainer}>
            <View
              style={[
                styles.statusBadge,
                pendingWaitUi && styles.statusPending,
                !pendingWaitUi && coupon.status === 'available' && styles.statusAvailable,
                !pendingWaitUi && coupon.status === 'used' && styles.statusUsed,
                !pendingWaitUi && coupon.status === 'expired' && styles.statusExpired
              ]}
            >
              <Text
                style={[
                  styles.statusText,
                  pendingWaitUi && styles.statusTextPending,
                  !pendingWaitUi && coupon.status === 'used' && styles.statusTextUsed,
                  !pendingWaitUi && coupon.status === 'available' && styles.statusTextAvailable,
                  !pendingWaitUi && coupon.status === 'expired' && styles.statusTextExpired,
                ]}
              >
                {pendingWaitUi
                  ? pendingWaitUi.statusLabel
                  : coupon.status === 'available'
                    ? 'Tersedia'
                    : coupon.status === 'used'
                      ? 'Digunakan'
                      : coupon.status === 'expired'
                        ? 'Kedaluwarsa'
                        : coupon.status === 'diklaim'
                          ? 'Diklaim'
                          : coupon.status}
              </Text>
            </View>
          </View>
        </View>

        {/* Voucher Button Section */}
        {renderVoucherButton()}

        {/* Terms and Conditions */}
        {coupon.syarat_ketentuan && (
          <View style={styles.termsContainer}>
            <Text style={styles.termsTitle}>Syarat & Ketentuan:</Text>
            <Text style={styles.termsText}>{coupon.syarat_ketentuan}</Text>
          </View>
        )}
      </ScrollView>

      {/* Custom Alert Modal */}
      <Modal
        visible={alertModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.alertModal}>
            <Text style={[
              styles.alertTitle,
              alertModal.type === 'success' && styles.alertTitleSuccess
            ]}>
              {alertModal.title}
            </Text>
            <Text style={styles.alertMessage}>{alertModal.message}</Text>
            <TouchableOpacity
              style={styles.alertButton}
              onPress={handleAlertConfirm}
            >
              <Text style={styles.alertButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  content: {
    flex: 1,
  },
  imageContainer: {
    width: '100%',
    height: 160,
    backgroundColor: '#FFFFFF',
  },
  couponImage: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
  },
  detailsContainer: {
    padding: 20,
    backgroundColor: '#FFFFFF',
  },
  couponName: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 8,
  },
  couponDescription: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 16,
    lineHeight: 22,
  },
  codeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  codeLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginRight: 8,
  },
  codeValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#059669',
    fontFamily: 'monospace',
  },
  expiryBlock: {
    marginBottom: 16,
  },
  expiryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  expiryText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
  },
  expiryPendingHint: {
    fontSize: 13,
    color: '#92400E',
    marginTop: 8,
    lineHeight: 18,
  },
  statusContainer: {
    marginBottom: 24,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusPending: {
    backgroundColor: '#FEF3C7',
  },
  statusAvailable: {
    backgroundColor: '#D1FAE5',
  },
  statusUsed: {
    backgroundColor: '#FEE2E2',
  },
  statusExpired: {
    backgroundColor: '#FEF3C7',
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  statusTextPending: {
    color: '#92400E',
  },
  statusTextUsed: {
    color: '#991B1B',
  },
  statusTextAvailable: {
    color: '#065F46',
  },
  statusTextExpired: {
    color: '#92400E',
  },
  voucherContainer: {
    padding: 20,
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    alignItems: 'center',
  },
  activateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2563EB',
    paddingVertical: 14,
    borderRadius: 8,
    gap: 8,
  },
  activateButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  useCouponButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderRadius: 12,
    minWidth: 200,
    marginTop: 16,
  },
  buttonIconSpacing: {
    marginRight: 8,
  },
  useCouponButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
  },
  voucherNote: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 12,
    lineHeight: 20,
  },
  pollStoppedNote: {
    fontSize: 13,
    color: '#92400E',
    textAlign: 'center',
    marginTop: 10,
    lineHeight: 18,
    fontWeight: '500',
  },
  voucherPlaceholder: {
    padding: 40,
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  voucherPlaceholderText: {
    fontSize: 16,
    color: '#94A3B8',
    marginTop: 16,
    textAlign: 'center',
  },
  voucherPlaceholderTextRejected: {
    color: '#B91C1C',
    fontWeight: '500',
  },
  termsContainer: {
    padding: 20,
    backgroundColor: '#FFFFFF',
    marginTop: 16,
  },
  termsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  termsText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  alertModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '80%',
    maxWidth: 400,
  },
  alertTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#DC2626',
    marginBottom: 12,
    textAlign: 'center',
  },
  alertTitleSuccess: {
    color: '#059669',
  },
  alertMessage: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  alertButton: {
    backgroundColor: '#3B82F6',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'center',
  },
  alertButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});